import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Actions from "../actions/Actions";
import styles from "./RevealSlide.module.scss";
import "react-pdf/dist/Page/TextLayer.css";
import Button from "@components/button/Button";
import { useCourseContent } from "@src/hooks/useCourseContent";
import { isHomeEDApp } from "@src/constants";
import RevealSlide from "@src/components/reveal-slide/RevealSlide";

type Props = {
  data: string;
  isFullScreen: boolean;
  isPreview?: boolean;
  goBack?: () => void;
  pageNumber?: number;
  changePageNumber?: (value: number) => void;
  startLesson?: () => void;
};

function RevealSlideView(props: Props) {
  const {
    data,
    isFullScreen,
    isPreview,
    pageNumber,
    changePageNumber,
    startLesson,
  } = props;
  const { totalPages, setTotalPages } = useCourseContent();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);

  const calculateSize = useCallback(() => {
    if (wrapperRef.current) {
      const { width, height } = wrapperRef?.current?.getBoundingClientRect();
      var w =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth;
      setWidth(w > width ? width : w);
      setHeight(height);
    }
  }, []);

  useLayoutEffect(() => {
    window.addEventListener("resize", calculateSize);

    calculateSize();
    return () => {
      window.removeEventListener("resize", calculateSize);
    };
  }, [calculateSize]);

  useEffect(() => {
    if (width) {
      setTimeout(calculateSize, 50);
    }
  }, [calculateSize, width]);

  const prevDisabled = useMemo(() => (pageNumber || 0) <= 1, [pageNumber]);
  const isLast = useMemo(
    () => (pageNumber || 0) >= totalPages,
    [pageNumber, totalPages]
  );

  const handleLeft = useCallback(() => {
    changePageNumber && changePageNumber(Math.max((pageNumber || 0) - 1, 1));
  }, [pageNumber, changePageNumber]);

  const handleRight = useCallback(() => {
    changePageNumber &&
      changePageNumber(Math.min((pageNumber || 0) + 1, totalPages));
  }, [totalPages, pageNumber, changePageNumber]);

  const prevButton = useMemo(() => {
    if (prevDisabled) return null;
    return (
      <Button
        size={"extraSmall"}
        onClick={handleLeft}
        className={styles.previousBtn}
        label={"PREVIOUS"}
      />
    );
  }, [prevDisabled, handleLeft]);

  const nextButton = useMemo(() => {
    if (isLast) return null;
    return <Button size={"extraSmall"} onClick={handleRight} label={"NEXT"} />;
  }, [isLast, handleRight]);

  return (
    <Actions
      fullScreen={isFullScreen}
      leftButton={prevButton}
      rightButton={nextButton}
      totalPages={totalPages}
      pageNumber={pageNumber}
    >
      <div
        className={`${styles.wrapper} ${
          !isPreview ? styles.blackfullScreen : ""
        }`}
        ref={wrapperRef}
      >
        <div className={isFullScreen ? styles.fullContent : styles.content}>
          <div style={{ width: `${width}px`, height: `${height}px` }}>
            <RevealSlide
              fullScreen={isFullScreen}
              isPreview={isPreview}
              key={data}
              data={data}
              pageNumber={pageNumber}
              totalPages={totalPages}
              setTotalPages={setTotalPages}
            />
          </div>
          {isPreview && !isHomeEDApp ? (
            <div className={styles.previewBeginLesson}>
              <div className={styles.pulse}>
                <Button
                  label={"Begin Lesson"}
                  color="white"
                  className={styles.beginLesson}
                  onClick={() => startLesson && startLesson()}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Actions>
  );
}

export default RevealSlideView;
